import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PoliticianData, BenchmarkData } from '../types';

interface ShareableCardProps {
  politician: PoliticianData;
  benchmarks: Record<string, BenchmarkData>;
  timeframe: string;
}

// Use inline styles for html2canvas compatibility
const styles = {
  card: {
    width: '1200px',
    height: '630px',
    background: '#fafaf9',
    position: 'relative' as const,
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  accent: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: '8px',
  },
  content: {
    display: 'flex',
    padding: '50px 60px 30px',
    height: '530px',
    boxSizing: 'border-box' as const,
  },
  left: {
    width: '380px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: '40px',
  },
  avatarContainer: {
    position: 'relative' as const,
    width: '180px',
    height: '180px',
    marginBottom: '24px',
  },
  avatar: {
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    background: '#e5e5e5',
    border: '4px solid white',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
  avatarRing: {
    position: 'absolute' as const,
    top: '-8px',
    left: '-8px',
    width: '196px',
    height: '196px',
    borderRadius: '50%',
    border: '3px solid',
    opacity: 0.6,
    boxSizing: 'border-box' as const,
  },
  name: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#1c1917',
    margin: '0 0 8px',
    textAlign: 'center' as const,
    fontFamily: 'Georgia, serif',
  },
  meta: {
    fontSize: '16px',
    color: '#57534e',
    textAlign: 'center' as const,
  },
  right: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    paddingLeft: '40px',
    borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
  },
  timeframeBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#57534e',
    marginBottom: '20px',
  },
  statLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#a8a29e',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '52px',
    fontWeight: 700,
    color: '#1c1917',
    lineHeight: 1.1,
    marginBottom: '4px',
    fontFamily: 'Georgia, serif',
  },
  returnPositive: {
    fontSize: '26px',
    fontWeight: 600,
    color: '#15803d',
    marginBottom: '24px',
  },
  returnNegative: {
    fontSize: '26px',
    fontWeight: 600,
    color: '#b91c1c',
    marginBottom: '24px',
  },
  returnLabel: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#a8a29e',
    marginLeft: '8px',
  },
  benchmarksRow: {
    display: 'flex',
    gap: '40px',
    marginBottom: '20px',
  },
  benchmarkLabel: {
    fontSize: '12px',
    color: '#a8a29e',
    fontWeight: 500,
    marginBottom: '4px',
  },
  benchmarkValuePositive: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#15803d',
  },
  benchmarkValueNegative: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#b91c1c',
  },
  holdingsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  holdingsLabel: {
    fontSize: '12px',
    color: '#a8a29e',
    fontWeight: 500,
  },
  holdingTag: {
    display: 'inline-block',
    padding: '6px 12px',
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1c1917',
    marginRight: '8px',
  },
  footer: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 60px',
    background: 'rgba(0, 0, 0, 0.03)',
    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
  },
  brandName: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1c1917',
    fontFamily: 'Georgia, serif',
  },
  brandTagline: {
    fontSize: '12px',
    color: '#a8a29e',
    marginTop: '2px',
  },
  url: {
    fontSize: '14px',
    color: '#57534e',
    fontWeight: 500,
  },
};

const ShareableCard = forwardRef<HTMLDivElement, ShareableCardProps>(
  ({ politician, benchmarks, timeframe }, ref) => {
    const { t, i18n } = useTranslation();

    const returnPct = ((politician.currentValue - 10000) / 10000) * 100;
    const isPositive = returnPct >= 0;

    const spyFinal = benchmarks.SPY?.data[benchmarks.SPY.data.length - 1]?.value || 10000;
    const qqqFinal = benchmarks.QQQ?.data[benchmarks.QQQ.data.length - 1]?.value || 10000;
    const spyReturn = ((spyFinal - 10000) / 10000) * 100;
    const qqqReturn = ((qqqFinal - 10000) / 10000) * 100;
    const vsSpy = returnPct - spyReturn;
    const vsQqq = returnPct - qqqReturn;

    const formatCurrency = (value: number): string => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    };

    const formatPercentage = (value: number): string => {
      const sign = value >= 0 ? '+' : '';
      return `${sign}${value.toFixed(1)}%`;
    };

    const getLocalizedName = () => {
      if (i18n.language.startsWith('zh')) {
        const chineseName = t(`politician:names.${politician.id}`, { defaultValue: '' });
        return chineseName ? `${chineseName} (${politician.name})` : politician.name;
      }
      return politician.name;
    };

    const partyColor = politician.party === 'Democrat' ? '#3b82f6' : '#ef4444';

    return (
      <div style={styles.card} ref={ref}>
        {/* Accent bar */}
        <div
          style={{
            ...styles.accent,
            background: `linear-gradient(135deg, ${politician.color}, ${politician.color}88)`,
          }}
        />

        {/* Main content */}
        <div style={styles.content}>
          {/* Left - Politician */}
          <div style={styles.left}>
            <div style={styles.avatarContainer}>
              <img
                src={`/images/${politician.id}.jpg`}
                alt={politician.name}
                style={styles.avatar}
                crossOrigin="anonymous"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/placeholder.png';
                }}
              />
              <div
                style={{
                  ...styles.avatarRing,
                  borderColor: politician.color,
                }}
              />
            </div>
            <div style={styles.name}>{getLocalizedName()}</div>
            <div style={styles.meta}>
              <span style={{ color: partyColor, fontWeight: 600 }}>
                {t(`politician:${politician.party.toLowerCase()}`)}
              </span>
              <span style={{ color: '#a8a29e', margin: '0 8px' }}>|</span>
              <span>{politician.state}</span>
              <span style={{ color: '#a8a29e', margin: '0 8px' }}>|</span>
              <span>{t(`politician:${politician.chamber.toLowerCase()}`)}</span>
            </div>
          </div>

          {/* Right - Stats */}
          <div style={styles.right}>
            <div style={styles.timeframeBadge}>{timeframe}</div>

            <div style={styles.statLabel}>SIMULATED PORTFOLIO VALUE</div>
            <div style={styles.statValue}>{formatCurrency(politician.currentValue)}</div>
            <div style={isPositive ? styles.returnPositive : styles.returnNegative}>
              {formatPercentage(returnPct)}
              <span style={styles.returnLabel}>from $10,000</span>
            </div>

            <div style={styles.benchmarksRow}>
              <div>
                <div style={styles.benchmarkLabel}>vs SPY</div>
                <div style={vsSpy >= 0 ? styles.benchmarkValuePositive : styles.benchmarkValueNegative}>
                  {formatPercentage(vsSpy)}
                </div>
              </div>
              <div>
                <div style={styles.benchmarkLabel}>vs QQQ</div>
                <div style={vsQqq >= 0 ? styles.benchmarkValuePositive : styles.benchmarkValueNegative}>
                  {formatPercentage(vsQqq)}
                </div>
              </div>
            </div>

            {politician.topHoldings && politician.topHoldings.length > 0 && (
              <div style={styles.holdingsRow}>
                <span style={styles.holdingsLabel}>Holdings:</span>
                <div>
                  {politician.topHoldings.slice(0, 3).map((holding) => (
                    <span key={holding.ticker} style={styles.holdingTag}>
                      {holding.ticker}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div>
            <div style={styles.brandName}>Capitol Alpha</div>
            <div style={styles.brandTagline}>Track Congressional Stock Trades</div>
          </div>
          <div style={styles.url}>capitol-alpha.com</div>
        </div>
      </div>
    );
  }
);

ShareableCard.displayName = 'ShareableCard';

export default ShareableCard;
